import { IndexedDBService } from '../../indexed-db/services/db_service';
import { VoyageImoModel } from '../models/voyage-imo-model';
import { ScheduleEtaChangeType } from '../models/schedule-change-model'
import { authFetchGET, authFetchPOST } from '../utils/auth-fetch'
import {
    AlarmPayload,
    VoyNotificationComposed,
    AlarmNotificationData,
    VoyNotification,
    BestPracticeRoutePayload,
    NotificationTypes,
    NotificationTypeToResponseData,
    VesselRouteSelectionNotification,
    VesselClfPopupNotification,
    mockLoadstarNotificationResponse,
} from './types/vessel-notifications-services.types'
import { VoyageBestPracticeRoutesModel } from '../models/voyage-best-practice-routes-model'
import { SchedulesService } from './schedules-service'
import { BprAdoption, VoyageRoute, WeatherRoutingInfo } from '../models/voyage-routing-model'
import { VesselPlansService } from './vessel-plans-service'

export interface BprRouteSelectionMetadata {
    bprId: string;
    departureTerminal: string;
    arrivalTerminal: string;
    createdAt: string;
    eta: string;
    etd: string;
    fuelRank: number | null;
    reasonForUpdate: string;
}

// A star route persisted on the plan itself, as opposed to BprRouteSelectionMetadata,
// which is resolved from the backend at approve time. This survives a post-departure
// approval: getOptimizedBPRsByPortPair returns nothing for the schedule by then, but
// the star route the navigator added during pre-planning is still on the plan.
//
// fuelRank and reasonForUpdate are deliberately absent — they exist only on the
// backend BprAPIResponse and are not carried onto a plan route.
export interface BprInPlanMetadata {
    bprId: string;
    routeId: string;
    name: string;
    // route.creationTime: re-stamped whenever the route is (re)calculated.
    addedAt: string | null;
    eta: string | null;
    etd: string | null;
    isSelected: boolean;
}

// RouteSelectionNotificationData is the 'Route' vessel-notification payload,
// built at send-and-approve. It is both posted to the notifications service and
// attached to plan.metadata.routeSelection so the plan is self-contained
// (mirrors plansapi api.RouteSelectionNotification).
export interface RouteSelectionNotificationData {
    planId: number;
    isWrAdviseReceived: boolean;
    isBPRCalculationCompleted: boolean;
    etd: number;
    eta: number;
    createdAtFrontendUnixUTC: number;
    scheduleId: string;
    routeDistanceNm: number;
    routeFuelConsumption: number;
    isMoreFuelEfficientThanStarRoute: boolean;
    isAlignedToStarRoute: boolean;
    bprCalculationLog: unknown;
    bprIDsInScheduleEntry: string[];
    // What the backend offered for this port pair and schedule at approve time.
    // Empty post-departure, when getOptimizedBPRsByPortPair no longer resolves.
    bprsOfferedToUser: BprRouteSelectionMetadata[];
    // What the navigator actually had on the plan. Derived from the plan's own
    // routes, so it stays populated regardless of when the plan is approved.
    bprsShownToUser: BprInPlanMetadata[];
    weatherRoutingInfo?: WeatherRoutingInfo;
    isVesselOnCloud: boolean;
    // Set when the plan was approved while offline: fetches were skipped and the
    // authoritative notification content is recomputed after background sync.
    isOffline?: boolean;
    // Set when collecting the data failed (fully or partially); the approve flow
    // proceeds regardless and the error travels with the plan metadata.
    captureError?: string;
}

// BprRejectNotificationData is the 'BprReject' vessel-notification payload
// attached to plan.metadata.bprReject at send-and-approve (mirrors plansapi
// api.BprRejectNotification).
export interface BprRejectNotificationData {
    reason?: string;
    comments?: string;
    navWarningNo?: string;
    scheduleId?: string;
    departureTerminal?: string;
    arrivalTerminal?: string;
    plansId?: number;
    eta?: number;
    etd?: number;
    bprRankIDs?: string[];
    captureError?: string;
}

const isVesselOnCloud = (<any>window)["appenv"].vesselOnCloud;
const notificationApi = (<any>window)["appenv"].services.notificationApi;
const notificationResponseApi = (<any>window)["appenv"].services.notificationResponseApi;
const env = { ...notificationApi, ...isVesselOnCloud ? { scope: (<any>window)["appenv"].scope } : {} };
const envResponse = { ...notificationResponseApi, ...isVesselOnCloud ? { scope: (<any>window)["appenv"].scope } : {} };

/**
 * Extract the star routes carried on a plan, for the route-selection metadata.
 *
 * Open Ocean routes cannot be excluded by uiType alone: both star routes and OORs
 * are built by _getRoute with a hardcoded uiType of 'BPR', and only
 * _addOORtoSchedule overwrites it to 'OOR' afterwards. _addBPRtoSchedule passes the
 * source BPR's uiType through for naming only, so an OOR arriving on that path keeps
 * uiType 'BPR' while being named "Open Ocean N". The name prefix is therefore the
 * reliable discriminator, and uiType 'OOR' is excluded as a second guard.
 */
export function getStarRoutesInPlan(routes: VoyageRoute[], currentSelectedRouteId?: string): BprInPlanMetadata[] {
    return (routes ?? [])
        .filter(route =>
            route.routeCreatedBy === 'system'
            && !!route.bestPracticeRouteId
            && route.uiType !== 'OOR'
            && !route.name?.startsWith('Open Ocean'))
        .map(route => ({
            bprId: route.bestPracticeRouteId!,
            routeId: route.routeId,
            name: route.name ?? '',
            addedAt: route.creationTime ?? null,
            eta: route.route_info?.eta ?? null,
            etd: route.route_info?.etd ?? null,
            isSelected: !!currentSelectedRouteId && route.routeId === currentSelectedRouteId,
        }));
}

export class VesselNotificationsService {

    private static getVesselNotificationsUrl(endpoint: string, isResponse = false) {
        const endPoint = (isResponse && isVesselOnCloud) ? envResponse.baseurl : env.baseurl;
        let url = `${endPoint}/VesselNotifications${endpoint}`
        if (isVesselOnCloud) {
            url += `${url.includes('?') ? '&' : '?'}imo_no=${VoyageImoModel!.imo}`
        }
        return url
    }

    static async fetchNotifications<T extends NotificationTypes>(
        type: T,
        section?: string,
    ): Promise<VoyNotification<T>[]> {
        let notifications: VoyNotification<T>[] = []

        try {
            //! Uncomment to get mock data in local development
            // const mockNotificationResponse = type === NotificationTypes.Alarm ? mockVoyAlarmNotificationFinal : mockVoyEtaDeviationNotificationResponse;
            // notifications = parseData<T>(mockNotificationResponse);  
            const started_at= new Date();
            const ended_at = new Date();
            if(section === 'log'){
                started_at.setDate(started_at.getDate() - 90);
            }
            else {
                started_at.setDate(started_at.getDate() - 15);
            }
            let query = section ==="log" ? this.getVesselNotificationsUrl(`?category=Voyage&type=${type}&started_at=${started_at.toISOString()}&ended_at=${ended_at.toISOString()}`) : this.getVesselNotificationsUrl(`?category=Voyage&type=${type}&state=Active&started_at=${started_at.toISOString()}&ended_at=${ended_at.toISOString()}`)            
            const resp = await authFetchGET(env.scope, query)
            notifications = parseData(resp)
                saveNotificationsToIndexedDB(notifications);
        } catch (err) {
            console.error(`error while fetching notifications`, err)
        }

        return notifications
    }
    static async fetchActiveNotifications<T extends NotificationTypes>(
        type: T,
    ) {
        let notifications=[]

        try {
            let query = this.getVesselNotificationsUrl(`?category=Voyage&type=${type}&state=Active`)
            notifications = await authFetchGET(env.scope, query)
            if (type !== NotificationTypes.EtaDeviation)
                saveNotificationsToIndexedDB(notifications);

        } catch (err) {
            console.error(`error while fetching active notifications`, err)
        }
        if (type !== NotificationTypes.EtaDeviation)
            saveNotificationsToIndexedDB(notifications);
        return notifications
    }

    static async fetchNotificationsByTypeAndState(type: NotificationTypes, state: string){
        let notifications: any[] = []

        try {
            let query = this.getVesselNotificationsUrl(`?category=Voyage&type=${type}&state=${state}`)
            let resp = await authFetchGET(env.scope, query)
            
            notifications = parseData(resp)

        } catch (err) {
            console.error(`error while fetching active notifications`, err)
        }
        return notifications
    }

    static async submitEtaDeviationReason(
        notificationId: string,
        reason: string,
        comment: string,
    ) {
        const payload = {
            state: 'Ended',
            // data is nested json, so have to stringify this prop
            data: JSON.stringify({
                reason: reason,
                comments: comment,
            }),
        }
        try {
            return await authFetchPOST(
                env.scope,
                this.getVesselNotificationsUrl(`/${notificationId}/Responses`, true),
                payload,
            )
        } catch (err) {
            console.error(`error while submitting the ETA deviation reason`, err)
        }
    }

    static async submitAlarmReason(
        payload: AlarmPayload,
        notificationId: string|number,
    ) {
        try {
            return await authFetchPOST(
                env.scope,
                this.getVesselNotificationsUrl(`/${notificationId}/Responses`, true),
                payload,
            )
        } catch (err) {
            console.error(`error while submitting the alarm reason`, err)
        }
    }

    static async submitFeedback(payload: any) {
        try {

            return await authFetchPOST(
                env.scope,
                this.getVesselNotificationsUrl('/'),
                {...payload, ...isVesselOnCloud ? { imoNo: VoyageImoModel?.imo! } : {}},
            )
        } catch (err) {
            console.error(`error while submitting the feedback`, err)
        }
    }

    static async createClfPopupNotification(
        scheduleId: string,
        departureTerminal: string,
        arrivalTerminal: string,
        plansId: number,
        { reason, comment, correlationId }: VesselClfPopupNotification,
        eta: string | number,
        etd: string | number,
        type: string,
    ) {
        try {
            const result = await authFetchPOST(
                env.scope,
                `${env.baseurl}/VesselNotifications/`,
                {
                    category: 'Voyage',
                    type: type,
                    data: JSON.stringify({
                        scheduleId,
                        departureTerminal,
                        arrivalTerminal,
                        plansId,
                        eta,
                        etd
                    }),
                },
            )

            /// throught the error if there an eeror with the call!
            if (!result.id) {
                throw new Error('Error creating notification')
            }

            const notificationId: string = result.id
            await authFetchPOST(
                env.scope,
                `${env.baseurl}/VesselNotifications/${notificationId}/Responses`,
                {
                    state: 'Active',
                    data: JSON.stringify({
                        reason: reason,
                        comments: comment,
                        correlationId: correlationId,
                    }),
                },
            )
        } catch (err) {
            console.error(
                `error while creating and submitting reason for choosing own route`,
                err,
            )
        }
    }

    static async createWniReportNotification(
        type: string,
        scheduleId: string,
        reportType: string,
    ){
        try {
            const result = await authFetchPOST(
                env.scope,
                `${env.baseurl}/VesselNotifications/`,
                {
                    category: 'Voyage',
                    type: type,
                    data: JSON.stringify({
                        scheduleID :scheduleId,
                        reportType: reportType,
                        reportID: 0
                    }),
                },
            )

            /// throught the error if there an eeror with the call!
            if (!result.id) {
                throw new Error('Error creating notification')
            }

        } catch (err) {
            console.error(
                `error while creating and submitting reason for choosing own route`,
                err,
            )
        }
    }

    static async createRouteSelectNotification(
        scheduleId: string,
        departureTerminal: string,
        arrivalTerminal: string,
        plansId: number,
        { reason, comment, navWarningNo}: VesselRouteSelectionNotification,
        eta: string | number,
        etd: string | number,
        bprRankIDs: string[],
    ) {
        const imo = isVesselOnCloud ? VoyageImoModel?.imo! : (<any>window)["appenv"].vesselIMONumber;

        const etaUnix = typeof eta === 'string' ? new Date(eta).getTime() : eta;
        const etdUnix = typeof etd === 'string' ? new Date(etd).getTime() : etd;

        const responseData = isVesselOnCloud ? JSON.stringify({
                            reason: reason,
                            comments: comment,
                            navWarningNo: navWarningNo
                        }) : null;

        try {
            const result = await authFetchPOST(
                env.scope,
                this.getVesselNotificationsUrl('/'),
                {
                    category: 'Voyage',
                    type: 'BprReject',
                    imoNo: imo,
                    data: JSON.stringify({
                        scheduleId,
                        departureTerminal,
                        arrivalTerminal,
                        plansId,
                        eta: etaUnix,
                        etd: etdUnix,
                        bprRankIDs,
                        ...JSON.parse(responseData || '{}')
                    }),
                },
            )

            if (!result.id) {
                throw new Error('Error creating notification')
            }

            const notificationId: string = result.id
            await authFetchPOST(
                env.scope,
                this.getVesselNotificationsUrl(`/${notificationId}/Responses`, true),
                {
                    state: 'Closed',
                    data: JSON.stringify({
                        reason: reason,
                        comments: comment,
                        navWarningNo: navWarningNo
                    }),
                },
            )
            console.log(`BprReject notification closed successfully for scheduleId:`, scheduleId, notificationId);
        } catch (err) {
            console.error(
                `error while creating and submitting reason for choosing own route`,
                err,
            )
        }
    }

    /**
     * Builds the 'Route' notification data without posting anything.
     *
     * NEVER THROWS and never blocks the approve flow: partial fetch failures are
     * recorded in `captureError` on the returned data, and a total failure returns
     * a minimal payload with `captureError` set. The returned object is also
     * attached to plan.metadata.routeSelection at send-and-approve, so shore can
     * read the notification (or why its capture failed) directly from the plan.
     *
     * When `offline` is true (vessel on cloud without connectivity) the backend
     * fetches are skipped entirely and `isOffline: true` is set: the authoritative
     * notification content is recomputed after background sync by
     * VesselBackgroundOptimizationModel, so this snapshot marks itself as such.
     */
    static async buildRouteNotificationData(
        scheduleId: string,
        planId: number,
        eta: string | number,
        etd: string | number,
        routeDistance: number,
        routeFuelConsumption: number,
        isAlignedToStarRoute: boolean,
        isMoreFuelEfficientThanStarRoute: boolean,
        departureTerminalCode: string,
        arrivalTerminalCode: string,
        weatherRoutingInfo?: WeatherRoutingInfo,
        offline: boolean = false,
        planRoutes: VoyageRoute[] = [],
        currentSelectedRouteId?: string,
    ): Promise<RouteSelectionNotificationData> {
        const etaUnix = typeof eta === 'string' ? new Date(eta).getTime() : eta;
        const etdUnix = typeof etd === 'string' ? new Date(etd).getTime() : etd;
        const baseData: RouteSelectionNotificationData = {
            planId: planId,
            isWrAdviseReceived: false,
            isBPRCalculationCompleted: false,
            etd: etdUnix,
            eta: etaUnix,
            createdAtFrontendUnixUTC: Date.now(),
            scheduleId: scheduleId,
            routeDistanceNm: routeDistance,
            routeFuelConsumption: routeFuelConsumption,
            isMoreFuelEfficientThanStarRoute: isMoreFuelEfficientThanStarRoute,
            isAlignedToStarRoute: isAlignedToStarRoute,
            bprCalculationLog: null,
            bprIDsInScheduleEntry: [],
            bprsOfferedToUser: [],
            // Derived from the plan, not fetched, so it is populated even offline and
            // even when this approve happens after departure.
            bprsShownToUser: getStarRoutesInPlan(planRoutes, currentSelectedRouteId),
            weatherRoutingInfo: weatherRoutingInfo,
            isVesselOnCloud: isVesselOnCloud,
        };
        const captureErrors: string[] = [];

        // Resolve the BPRs the backend offers for this port pair and schedule. Do not
        // put this fallback in the shared port-pair service: its other callers must
        // see the endpoint's authoritative null/empty response.
        try {
            let offeredBPRs = await VesselPlansService.getOptimizedBPRsByPortPair(
                    departureTerminalCode,
                    arrivalTerminalCode,
                    scheduleId,
                );

            if (!offeredBPRs?.length && scheduleId) {
                const cachedBPRs = await IndexedDBService.bprs.get(scheduleId);
                offeredBPRs = cachedBPRs?.data?.length ? cachedBPRs.data : null;
            }

            baseData.bprsOfferedToUser = (offeredBPRs ?? []).map(bpr => ({
                bprId: bpr.best_practice_route_id,
                departureTerminal: bpr.departure_terminal,
                arrivalTerminal: bpr.arrival_terminal,
                createdAt: bpr.create_at,
                eta: bpr.eta,
                etd: bpr.etd,
                fuelRank: bpr.fuel_rank ?? bpr.rank ?? null,
                reasonForUpdate: bpr.reason_for_update,
            }));
        } catch (err) {
            console.warn('Could not resolve offered BPR details for route metadata:', err);
            captureErrors.push('failed to resolve offered BPR details');
        }

        if (offline) {
            // Skip the remaining backend fetches while offline; mark the snapshot
            // so shore knows the full notification content was finalized post-sync.
            baseData.isOffline = true;
            if (captureErrors.length > 0) {
                baseData.captureError = captureErrors.join('; ');
            }
            return baseData;
        }
        try {
            const vesselplansApi = (<any>window)['appenv'].services.vesselplansapi;
            const plansApiEnv = { ...vesselplansApi, ...isVesselOnCloud ? { scope: (<any>window)["appenv"].scope } : {} };

            // Get BPR calculation logs
            let bprCalculationLog = null;
            try {
                bprCalculationLog = await authFetchGET(
                    plansApiEnv.scope,
                    `${plansApiEnv.baseurl}/bpr/calculation-logs`,
                    { scheduleID: scheduleId }
                );
            } catch (err) {
                console.warn('Could not fetch BPR calculation logs:', err);
                captureErrors.push('failed to fetch BPR calculation logs');
            }

            // Check if WR advise is received
            let isWrAdviseReceived = false;
            if (!isVesselOnCloud) {
                try {
                    const wrAdviseResponse = await authFetchGET(
                        plansApiEnv.scope,
                        `${plansApiEnv.baseurl}/wradvise/schedule/${scheduleId}`
                    );
                    isWrAdviseReceived = wrAdviseResponse && wrAdviseResponse.route !== null;
                } catch (err) {
                    console.warn('Could not check WR advise status:', err);
                    captureErrors.push('failed to check WR advise status');
                }
            }

            // Check if there's an ongoing BPR calculation job
            const hasPendingBPRJob = VoyageBestPracticeRoutesModel?.hasPendingBPRJob(scheduleId) ?? false;

            // Check if BPR calculation is completed:
            // - False if there's a pending job AND no logs exist (calculation ongoing)
            // - True otherwise (either logs exist OR no pending job, meaning calculation finished or never started)
            const isBPRCalculationCompleted =
                !(hasPendingBPRJob && (bprCalculationLog === null || bprCalculationLog === undefined));

            // Get BPR IDs per schedule
            let bprIDsInScheduleEntry: string[] = [];
            try {
                bprIDsInScheduleEntry = await SchedulesService.getBPRIDListPerSchedule(departureTerminalCode, arrivalTerminalCode, scheduleId);
            } catch (err) {
                console.warn('Could not fetch BPR IDs per schedule:', err);
                captureErrors.push('failed to fetch BPR IDs per schedule');
            }

            baseData.isWrAdviseReceived = isWrAdviseReceived;
            baseData.isBPRCalculationCompleted = isBPRCalculationCompleted;
            baseData.bprCalculationLog = bprCalculationLog;
            baseData.bprIDsInScheduleEntry = bprIDsInScheduleEntry;
        } catch (err) {
            console.error('error while building route notification data', err);
            captureErrors.push(`unexpected error building route notification data: ${err instanceof Error ? err.message : String(err)}`);
        }
        if (captureErrors.length > 0) {
            baseData.captureError = captureErrors.join('; ');
        }
        return baseData;
    }

    /**
     * Posts a previously built 'Route' notification (create + Active response).
     * Returns the BprAdoption subset on success, or null on failure (the approve
     * flow must not be blocked by notification submission errors).
     */
    static async submitRouteNotificationData(
        data: RouteSelectionNotificationData,
    ): Promise<Pick<BprAdoption, 'isAlignedToStarRoute' | 'isMoreFuelEfficientThanStarRoute' | 'isWrAdviseReceived' | 'isBPRCalculationCompleted' | 'bprIDsInScheduleEntry' | 'routeDistanceNm' | 'routeFuelConsumption'> | null> {
        try {
            const imo = isVesselOnCloud ? VoyageImoModel?.imo! : (<any>window)["appenv"].vesselIMONumber;
            const result = await authFetchPOST(
                env.scope,
                `${env.baseurl}/VesselNotifications/`,
                {
                    category: 'Voyage',
                    type: 'Route',
                    imoNo: imo,
                    data: JSON.stringify(data),
                },
            )

            if (!result.id) {
                throw new Error('Error creating route notification')
            }

            const notificationId: string = result.id
            await authFetchPOST(
                env.scope,
                `${env.baseurl}/VesselNotifications/${notificationId}/Responses`,
                {
                    state: 'Active',
                    data: JSON.stringify(data),
                },
            )

            return {
                isAlignedToStarRoute: data.isAlignedToStarRoute,
                isMoreFuelEfficientThanStarRoute: data.isMoreFuelEfficientThanStarRoute,
                isWrAdviseReceived: data.isWrAdviseReceived,
                isBPRCalculationCompleted: data.isBPRCalculationCompleted,
                bprIDsInScheduleEntry: data.bprIDsInScheduleEntry,
                routeDistanceNm: data.routeDistanceNm,
                routeFuelConsumption: data.routeFuelConsumption,
            };
        } catch (err) {
            console.error(
                `error while creating and submitting best practice route notification`,
                err,
            )
            return null;
        }
    }

    static async createAndSubmitRouteNotification(
        scheduleId: string,
        planId: number,
        eta: string | number,
        etd: string | number,
        routeDistance: number,
        routeFuelConsumption: number,
        isAlignedToStarRoute: boolean,
        isMoreFuelEfficientThanStarRoute: boolean,
        departureTerminalCode: string,
        arrivalTerminalCode: string,
        weatherRoutingInfo?: WeatherRoutingInfo,
        planRoutes: VoyageRoute[] = [],
        currentSelectedRouteId?: string,
    ): Promise<Pick<BprAdoption, 'isAlignedToStarRoute' | 'isMoreFuelEfficientThanStarRoute' | 'isWrAdviseReceived' | 'isBPRCalculationCompleted' | 'bprIDsInScheduleEntry' | 'routeDistanceNm' | 'routeFuelConsumption'> | null> {
        const data = await this.buildRouteNotificationData(
            scheduleId,
            planId,
            eta,
            etd,
            routeDistance,
            routeFuelConsumption,
            isAlignedToStarRoute,
            isMoreFuelEfficientThanStarRoute,
            departureTerminalCode,
            arrivalTerminalCode,
            weatherRoutingInfo,
            false,
            planRoutes,
            currentSelectedRouteId,
        );
        return this.submitRouteNotificationData(data);
    }

    static async fetchAlerts<T extends NotificationTypes>(
        type: T,
    ): Promise<VoyNotification<T>[]> {
        let notifications: VoyNotification<T>[] = []
        try {
            //! Uncomment to get mock data in local development
            // const mockNotificationResponse = type === NotificationTypes.Alarm ? mockVoyAlarmNotificationFinal : mockVoyEtaDeviationNotificationResponse;
            // notifications = parseData<T>(mockNotificationResponse);

            let query = this.getVesselNotificationsUrl(`?state=Active&category=Voyage&type=eta-alerts`)

            const response = await authFetchGET(env.scope, query)

            const alertsWithoutResponses = response.filter(
                (notification: { notification_responses: string | any[] }) =>
                    notification.notification_responses.length === 0,
            )
            notifications = parseData(alertsWithoutResponses)
            if (type !== NotificationTypes.EtaDeviation)
                saveNotificationsToIndexedDB(notifications);
        } catch (err) {
            console.error(`error while fetching notifications`, err)
        }
        return notifications
    }

    static async acceptAlerts(notificationId: string) {
        let payload = {
            state: 'Acknowledged',
            data: JSON.stringify({
                acknowledged_date: new Date().toISOString(),
            }),
        }
        try {
            return await authFetchPOST(
                env.scope,
                this.getVesselNotificationsUrl(`/${notificationId}/Responses`, true),
                payload,
            )
        } catch (err) {
            console.error(`error while accepting alert`, err)
        }
    }

    static async fetchAlert(notificationId: string | number) {

        try {
            const response = await authFetchGET(
                env.scope,
                this.getVesselNotificationsUrl(`/${notificationId}`),
            );
            if (response){
                await saveNotificationsToIndexedDB([response]);
            }
            return response
        } catch (err) {
            console.error(`error while fetching the notification data`, err)
        }
    }

    static async closeNotification(notificationId: string) {
        let payload = {
            state: 'Closed',
            data: JSON.stringify({
                closed_date: new Date().toISOString(),
                type: 'plan-type-original',
                reason: 'Vessel Acknowledged the Original Plan from WNI',
            }),
        }
        try {
            return await authFetchPOST(
                env.scope,
                `${env.baseurl}/VesselNotifications/${notificationId}/Responses`,
                payload,
            )
        } catch (err) {
            console.error(`error while closing the notification`, err)
        }
    }
    
    static async postScheduleChangeAlerts(notificationId: string, state: string, data: any) {
        let payload = {
            state: state,
            data: JSON.stringify(data),
        }
        try {
            return await authFetchPOST(
                env.scope,
                `${env.baseurl}/VesselNotifications/${notificationId}/Responses`,
                payload,
            )
        } catch (err) {
            console.error(`error while submitting the alarm reason`, err)
        }
    }

    static async postImminentRiskAudioAlerts(notificationId: string, state: string, audioHeard: boolean) {
        let payload = {
            state: state,
            data: JSON.stringify({ audioEnabled: audioHeard }),
        }
        try {
            return await authFetchPOST(
                env.scope,
                `${env.baseurl}/VesselNotifications/${notificationId}/Responses`,
                payload,
            )
        } catch (err) {
            console.error(`error while submitting the alarm reason`, err)
        }
    }
    
    //check health of notifications API
    async checkMetricsHealth(): Promise<boolean> {
        const baseUrl = env.baseurl.replace(/\/(api\/)?v1\/?$/, '');
        const url = `${baseUrl}/heartbeat`;
        const timeout = 5000; // 5 seconds timeout
       
        try {
            const response = await Promise.race([
                authFetchGET(env.scope, url), // assuming this returns a Response object
                new Promise<never>((_, reject) =>
                    setTimeout(() => reject(new Error('Request timed out')), timeout)
                )
            ]);

            // Just check for HTTP 200 OK
            if (response.status === 200) {
                return true;
            } else {
                console.error(`Metrics endpoint returned status: ${response.status}`);
                return false;
            }
        } catch (error) {
            if (error instanceof Error) {
                console.error("Metrics health check failed:", error.message);
            }
            return false;
        }
    }

    static async createEtaRequestNotification(payload: any): Promise<any> {
        try {
            let url = `${env.baseurl}/VesselNotifications?type=${NotificationTypes.RequestETAChange}`;

            const result = await authFetchPOST(
                env.scope,
                url,
                {
                    category: 'Voyage',
                    type: NotificationTypes.RequestETAChange,
                    ...(isVesselOnCloud ? { ImoNo: VoyageImoModel?.imo } : {}),
                    data: JSON.stringify(payload),
                }
            );
            return result;
        } catch (err) {
            console.error(
                'Error while creating and submitting ETA request notification',
                err
            );
            throw err; 
        }
    }   

    static async createImminentRiskOnboardNotification(audioHeard: boolean): Promise<any> {
        try {
            const result = await authFetchPOST(
                env.scope,
                `${env.baseurl}/VesselNotifications/`,
                {
                    category: 'Voyage',
                    type: 'imminent-risk-onboard',
                    data: JSON.stringify({ audioEnabled: audioHeard }),
                }
            );
            return result;
        } catch (err) {
            console.error(
                'Error while creating and submitting Imminent Risk Onboard notification',
                err
            );
            throw err; 
        }
    }

    static async acknowledgeImminentRiskAlert(notificationId: string, state: string) {
        let payload = {
            state: state,
            data: JSON.stringify({
                message: "Vessel Acknowledged the Imminent Risk Alert"
            })
        }
        try {
            return await authFetchPOST(
                env.scope,
                `${env.baseurl}/VesselNotifications/${notificationId}/Responses`,
                payload,
            )
        } catch (err) {
            console.error(`error while submitting the alarm reason`, err)
        }
    }

    static async acceptOrRejectLoadStarNotification(notificationId: string, state: string) {
        let payload = {
            state: state,
            data: JSON.stringify({
                message: `Vessel ${state} the LoadStar Alert`
            })
        }
        try {
            return await authFetchPOST(
                env.scope,
                `${env.baseurl}/VesselNotifications/${notificationId}/Responses`,
                payload,
            )
        } catch (err) {
            console.error(`error while submitting the alarm reason`, err)
        }
    }
}


// Notifications contain JSON in the `data` props on both notification.data and
// notification.notification_reponse, so need to parse these
function parseData<T extends NotificationTypes>(
    notifications: VoyNotificationComposed[] = [],
): VoyNotification<T>[] {
    const results: VoyNotification<T>[] = []
    for (const notification of notifications) {
        let parsedNotificationData: AlarmNotificationData
        try {
            parsedNotificationData = JSON.parse(notification.data) as AlarmNotificationData
        } catch (err) {
            console.error(`Failed to parse notification data for id=${notification.id}`, err)
            continue
        }

        const parsedNotificationResponses: any = []
        for (const response of notification.notification_responses) {
            try {
                parsedNotificationResponses.push({
                    ...response,
                    data: JSON.parse(response.data) as NotificationTypeToResponseData[T],
                    type: notification.type as T,
                })
            } catch (err) {
                console.error(`Failed to parse response data for notification id=${notification.id}`, err)
            }
        }

        results.push({
            ...notification,
            data: parsedNotificationData,
            notification_responses: parsedNotificationResponses,
        } as VoyNotification<T>)
    }
    return results
}

/**
 * Saves an array of notifications to IndexedDB
 * @param notifications - Array of notification objects to save
 * @returns Promise that resolves when all notifications are saved
 */
async function saveNotificationsToIndexedDB(notifications: any[]) {
    try {
        const savePromises = notifications.map(notification => {
            return IndexedDBService.notifications.add(notification);
        });
        
        await Promise.all(savePromises);
    } catch (dbErr) {
        console.error(`Error saving notifications to IndexedDB`, dbErr);
        throw dbErr;
    }
}
